import { useServices } from "@hq/client-react";
import type { Task } from "@hq/contracts";
import { type NowContext, parseQuickAdd, type QuickAddResult } from "@hq/core";
import { Badge, type BadgeProps, cn, Input, Kbd } from "@z/ui";
import {
  CalendarClock,
  CalendarDays,
  Flag,
  Folder,
  Hash,
  Moon,
  Plus,
  Sunrise,
  Tag,
  Timer,
} from "lucide-react";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import { formatDueDate, formatDuration, formatScheduled } from "./format";
import { useNow } from "./useNow";
import { useProjectIndex } from "./useProjectNames";

export interface QuickAddProps {
  /** Project a new task lands in when the text names none. */
  projectId?: string;
  sourceNoteId?: string;
  triaged?: boolean;
  plannedDate?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onCreated?: (task: Task) => void;
  /** Pins the clock; defaults to the shared ticking clock. */
  now?: NowContext;
  className?: string;
}

type Chip = { key: string; label: string; icon: ReactNode; variant: BadgeProps["variant"] };

const PRIORITY_LABEL = { high: "High priority", medium: "Medium priority", low: "Low priority" };
const PRIORITY_VARIANT = { high: "danger", medium: "warning", low: "accent" } as const;

/** Chips describing what the parser recognised, derived from the parse result on every render. */
export function chipsFor(
  parsed: QuickAddResult,
  now: NowContext,
  projects: readonly { id: string; name: string }[],
): Chip[] {
  const chips: Chip[] = [];
  if (parsed.plannedDate)
    chips.push({
      key: "when",
      label: `When ${formatDueDate(parsed.plannedDate, now)}`,
      icon: <CalendarClock />,
      variant: "accent",
    });
  if (parsed.dueDate !== undefined) {
    chips.push({
      key: "deadline",
      label: `Deadline ${formatDueDate(parsed.dueDate, now)}`,
      icon: <CalendarDays />,
      variant: "accent",
    });
  }
  if (parsed.scheduledAt !== undefined) {
    chips.push({
      key: "scheduled",
      label: `Scheduled ${formatScheduled(parsed.scheduledAt, now)}`,
      icon: <CalendarClock />,
      variant: "accent",
    });
  }
  if (parsed.scheduledDurationMinutes !== undefined) {
    chips.push({
      key: "duration",
      label: formatDuration(parsed.scheduledDurationMinutes),
      icon: <Timer />,
      variant: "default",
    });
  }
  if (parsed.priority !== undefined) {
    chips.push({
      key: "priority",
      label: PRIORITY_LABEL[parsed.priority],
      icon: <Flag />,
      variant: PRIORITY_VARIANT[parsed.priority],
    });
  }
  if (parsed.projectName !== undefined) {
    const wanted = parsed.projectName.toLowerCase();
    const match = projects.find((p) => p.name.toLowerCase() === wanted);
    chips.push(
      match
        ? {
            key: "project",
            label: match.name,
            icon: <Folder />,
            variant: "default",
          }
        : {
            key: "project",
            label: `${parsed.projectName} · no such project`,
            icon: <Hash />,
            variant: "outline",
          },
    );
  }
  for (const label of parsed.labels) {
    chips.push({ key: `tag:${label}`, label: `Tag: ${label}`, icon: <Tag />, variant: "default" });
  }
  if (parsed.evening) {
    chips.push({ key: "evening", label: "This evening", icon: <Moon />, variant: "default" });
  }
  if (parsed.status === "someday") {
    chips.push({ key: "someday", label: "Someday", icon: <Sunrise />, variant: "outline" });
  }
  return chips;
}

/**
 * The capture box. Parses on every keystroke (pure, during render) and shows what it
 * understood as chips below the input. Enter creates the task locally and keeps focus so
 * the next capture starts immediately. Escape clears.
 */
export function QuickAdd({
  projectId,
  sourceNoteId,
  triaged,
  plannedDate,
  placeholder = "Add a task… try “Pay rent tomorrow 9am p1 #Personal”",
  autoFocus,
  onCreated,
  now: nowOverride,
  className,
}: QuickAddProps) {
  const services = useServices();
  const now = useNow(nowOverride);
  const { projects } = useProjectIndex();
  const [text, setText] = useState("");

  const parsed = parseQuickAdd(text, now);
  const capture =
    plannedDate && !parsed.plannedDate && !parsed.scheduledAt && parsed.status !== "someday"
      ? { ...parsed, plannedDate }
      : parsed;
  const chips = text.trim() ? chipsFor(capture, now, projects) : [];
  const canSubmit = parsed.title.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    const value = text;
    setText("");
    const opts = {
      projects,
      ...(triaged ? { triaged: true } : {}),
      ...(projectId ? { projectId } : {}),
      ...(sourceNoteId ? { sourceNoteId } : {}),
      ...(plannedDate ? { plannedDate } : {}),
    };
    void services.tasks.createFromQuickAdd(value, opts).then((task) => onCreated?.(task));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setText("");
    }
  };

  return (
    <div data-slot="quick-add" className={cn("flex flex-col gap-1.5", className)}>
      <div className="relative">
        <Plus
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
          strokeWidth={1.75}
        />
        <Input
          data-slot="quick-add-input"
          aria-label="Add a task"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          className="h-11 border-border-strong bg-surface pl-9 pr-12 shadow-xs placeholder:text-fg-muted hover:border-fg-faint focus-visible:border-accent focus-visible:bg-bg"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center"
        >
          <Kbd>{canSubmit ? "↵" : "c"}</Kbd>
        </span>
      </div>
      {chips.length > 0 && (
        <div data-slot="quick-add-chips" className="flex flex-wrap items-center gap-1 px-0.5">
          {chips.map((chip) => (
            <Badge key={chip.key} variant={chip.variant} data-chip={chip.key}>
              {chip.icon}
              {chip.label}
            </Badge>
          ))}
          {parsed.title.length === 0 && (
            <span className="text-xs text-fg-faint">Add a title to create the task</span>
          )}
        </div>
      )}
    </div>
  );
}
